import React from 'react';
import { createContext, useContext, useCallback, useState } from 'react';
import type { Task, Notebook, Note, TaskCategory, Idea, IdeaFeature, Decision, DecisionOption, DecisionOptionPoint } from '../types/database';
import * as api from '../lib/api';
import { useAuth } from './AuthContext';
import {
  createProject as apiCreateProject,
  getIdeaFeatures as apiGetIdeaFeatures,
  createTask as apiCreateTask,
} from '../lib/api';

interface SupabaseContextType {
  // Tasks
  tasks: Task[];
  loadTasks: (category?: TaskCategory) => Promise<void>;
  createTask: (
    task: Omit<Task, 'id' | 'created_at' | 'modified_at' | 'user_id'> & { user_id?: string }
  ) => Promise<Task>;
  updateTask: (id: string, task: Partial<Omit<Task, 'id' | 'created_at' | 'modified_at'>>) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
  promoteTaskToProject: (id: string) => Promise<void>;
  demoteProjectToTask: (id: string) => Promise<void>;
  
  // Notebooks
  notebooks: Notebook[];
  loadNotebooks: () => Promise<void>;
  createNotebook: (notebook: Omit<Notebook, 'id' | 'created_at'>) => Promise<Notebook>;
  updateNotebook: (id: string, notebook: Partial<Omit<Notebook, 'id' | 'created_at'>>) => Promise<Notebook>;
  deleteNotebook: (id: string) => Promise<void>;
  
  // Notes
  notes: Record<string, Note[]>;
  loadNotes: (notebookId: string) => Promise<void>;
  createNote: (note: Omit<Note, 'id' | 'created_at' | 'modified_at'>) => Promise<Note>;
  updateNote: (id: string, note: Partial<Omit<Note, 'id' | 'created_at' | 'modified_at'>>) => Promise<Note>;
  deleteNote: (id: string) => Promise<void>;

  // Projects
  projects: any[];
  loadProjects: () => Promise<void>;
  createProject: (project: any) => Promise<any>;
  updateProject: (id: string, project: any) => Promise<any>;
  deleteProject: (id: string) => Promise<void>;

  // Ideas
  ideas: Idea[];
  loadIdeas: () => Promise<void>;
  createIdea: (
    idea: Omit<Idea, 'id' | 'created_at' | 'modified_at' | 'user_id'> & { user_id?: string }
  ) => Promise<Idea>;
  updateIdea: (id: string, idea: Partial<Omit<Idea, 'id' | 'created_at' | 'modified_at'>>) => Promise<Idea>;
  deleteIdea: (id: string) => Promise<void>;
  createIdeaFeature: (feature: IdeaFeature) => Promise<IdeaFeature>;
  updateIdeaFeature: (id: string, feature: Partial<IdeaFeature>) => Promise<IdeaFeature>;
  deleteIdeaFeature: (id: string) => Promise<void>;
  promoteIdeaToProject: (id: string) => Promise<void>;
  loadIdeaFeatures: (ideaId: string) => Promise<void>;
  ideaFeatures: Record<string, IdeaFeature[]>;

  // Decision Tree
  decisions: Decision[];
  loadDecisions: () => Promise<void>;
  createDecision: (decision: Omit<Decision, 'id' | 'created_at' | 'updated_at' | 'user_id'> & { user_id?: string }) => Promise<Decision>;
  updateDecision: (id: string, decision: Partial<Omit<Decision, 'id' | 'created_at' | 'updated_at' | 'user_id'>>) => Promise<Decision>;
  deleteDecision: (id: string) => Promise<void>;
  decisionOptions: Record<string, DecisionOption[]>;
  loadDecisionOptions: (decisionId: string) => Promise<void>;
  createDecisionOption: (option: Omit<DecisionOption, 'id' | 'created_at' | 'updated_at'>) => Promise<DecisionOption>;
  updateDecisionOption: (id: string, option: Partial<Omit<DecisionOption, 'id' | 'created_at' | 'updated_at'>>) => Promise<DecisionOption>;
  deleteDecisionOption: (id: string) => Promise<void>;
  decisionOptionPoints: Record<string, DecisionOptionPoint[]>;
  loadDecisionOptionPoints: (optionId: string) => Promise<void>;
  createDecisionOptionPoint: (point: Omit<DecisionOptionPoint, 'id' | 'created_at'>) => Promise<DecisionOptionPoint>;
  updateDecisionOptionPoint: (id: string, point: Partial<Omit<DecisionOptionPoint, 'id' | 'created_at'>>) => Promise<DecisionOptionPoint>;
  deleteDecisionOptionPoint: (id: string) => Promise<void>;
}

const SupabaseContext = createContext<SupabaseContextType | null>(null);

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export const SupabaseProvider = ({ children }: SupabaseProviderProps) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [notes, setNotes] = useState<Record<string, Note[]>>({});
  const [projects, setProjects] = useState<any[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [ideaFeatures, setIdeaFeatures] = useState<Record<string, IdeaFeature[]>>({});
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [decisionOptions, setDecisionOptions] = useState<Record<string, DecisionOption[]>>({});
  const [decisionOptionPoints, setDecisionOptionPoints] = useState<Record<string, DecisionOptionPoint[]>>({});

  // Tasks
  const loadTasks = useCallback(async (category?: TaskCategory) => {
    try {
      const fetchedTasks = await api.getTasks(category, user?.id);
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Error loading tasks', error);
    }
  }, [user?.id]);

  const createTask = useCallback(
    async (
      task: Omit<Task, 'id' | 'created_at' | 'modified_at' | 'user_id'> & {
        user_id?: string;
      }
    ) => {
      const payload = {
        ...task,
        user_id: task.user_id ?? user?.id,
      } as any;
      const newTask = await api.createTask(payload);
      setTasks((prev) => [...prev, newTask]);
      return newTask;
    },
    [user]
  );

  const updateTask = useCallback(async (id: string, task: Partial<Omit<Task, 'id' | 'created_at' | 'modified_at'>>) => {
    const updatedTask = await api.updateTask(id, task);
    setTasks(prev => prev.map(t => t.id === id ? updatedTask : t));
    return updatedTask;
  }, []);

  const deleteTask = useCallback(async (id: string) => {
    await api.deleteTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
  }, []);

  // Promotion helpers
  const promoteTaskToProject = useCallback(async (id: string) => {
    // Re‑use updateTask to flip the category
    await updateTask(id, { category: 'project' });
  }, [updateTask]);

  const demoteProjectToTask = useCallback(async (id: string) => {
    await updateTask(id, { category: 'task' });
  }, [updateTask]);

  // Notebooks
  const loadNotebooks = useCallback(async () => {
    const notebooks = await api.getNotebooks();
    setNotebooks(notebooks);
  }, []);

  const createNotebook = useCallback(async (notebook: Omit<Notebook, 'id' | 'created_at'>) => {
    const newNotebook = await api.createNotebook(notebook);
    setNotebooks(prev => [...prev, newNotebook]);
    return newNotebook;
  }, []);

  const updateNotebook = useCallback(async (id: string, notebook: Partial<Omit<Notebook, 'id' | 'created_at'>>) => {
    const updatedNotebook = await api.updateNotebook(id, notebook);
    setNotebooks(prev => prev.map(n => n.id === id ? updatedNotebook : n));
    return updatedNotebook;
  }, []);

  const deleteNotebook = useCallback(async (id: string) => {
    await api.deleteNotebook(id);
    setNotebooks(prev => prev.filter(n => n.id !== id));
    setNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[id];
      return newNotes;
    });
  }, []);

  // Notes
  const loadNotes = useCallback(async (notebookId: string) => {
    const notes = await api.getNotes(notebookId);
    setNotes(prev => ({ ...prev, [notebookId]: notes }));
  }, []);

  const createNote = useCallback(async (note: Omit<Note, 'id' | 'created_at' | 'modified_at'>) => {
    const newNote = await api.createNote(note);
    setNotes(prev => ({
      ...prev,
      [note.notebook_id]: [...(prev[note.notebook_id] || []), newNote]
    }));
    return newNote;
  }, []);

  const updateNote = useCallback(async (id: string, note: Partial<Omit<Note, 'id' | 'created_at' | 'modified_at'>>) => {
    const updatedNote = await api.updateNote(id, note);
    setNotes(prev => {
      const notebookNotes = prev[updatedNote.notebook_id] || [];
      return {
        ...prev,
        [updatedNote.notebook_id]: notebookNotes.map(n => n.id === id ? updatedNote : n)
      };
    });
    return updatedNote;
  }, []);

  const deleteNote = useCallback(async (id: string) => {
    await api.deleteNote(id);
    setNotes(prev => {
      const newNotes = { ...prev };
      Object.keys(newNotes).forEach(notebookId => {
        newNotes[notebookId] = newNotes[notebookId].filter(n => n.id !== id);
      });
      return newNotes;
    });
  }, []);

  // Projects
  const loadProjects = useCallback(async () => {
    try {
      const p = await api.getProjects();
      setProjects(p);
    } catch (err) {
      console.error('Error loading projects', err);
    }
  }, []);

  const createProject = useCallback(async (proj: any) => {
    const payload = user ? { ...proj, created_by: user.id } : proj;
    const newProj = await api.createProject(payload);
    setProjects(prev => [...prev, newProj]);
    return newProj;
  }, [user]);

  const updateProject = useCallback(async (id: string, proj: any) => {
    const updated = await api.updateProject(id, proj);
    setProjects(prev => prev.map(p => p.id === id ? updated : p));
    return updated;
  }, []);

  const deleteProject = useCallback(async (id: string) => {
    await api.deleteProject(id);
    setProjects(prev => prev.filter(p => p.id !== id));
  }, []);

  // Ideas
  const loadIdeas = useCallback(async () => {
    try {
      const fetchedIdeas = await api.getIdeas();
      setIdeas(fetchedIdeas);
    } catch (error) {
      console.error('Error loading ideas', error);
    }
  }, []);

  const createIdea = useCallback(
    async (
      idea: Omit<Idea, 'id' | 'created_at' | 'modified_at' | 'user_id'> & {
        user_id?: string;
      }
    ) => {
      const payload = {
        ...idea,
        user_id: idea.user_id ?? user?.id,
      } as any;
      const newIdea = await api.createIdea(payload);
      setIdeas((prev) => [...prev, newIdea]);
      return newIdea;
    },
    [user]
  );

  const updateIdea = useCallback(async (id: string, idea: Partial<Omit<Idea, 'id' | 'created_at' | 'modified_at'>>) => {
    const updatedIdea = await api.updateIdea(id, idea);
    setIdeas(prev => prev.map(i => i.id === id ? updatedIdea : i));
    return updatedIdea;
  }, []);

  const deleteIdea = useCallback(async (id: string) => {
    await api.deleteIdea(id);
    setIdeas(prev => prev.filter(i => i.id !== id));
  }, []);

  // Idea Features
  const loadIdeaFeatures = useCallback(async (ideaId: string) => {
    try {
      const features = await api.getIdeaFeatures(ideaId);
      setIdeaFeatures(prev => ({ ...prev, [ideaId]: features }));
    } catch (err) {
      console.error('Error loading idea features', err);
    }
  }, []);

  const createIdeaFeature = useCallback(async (feature: IdeaFeature) => {
    const newFeature = await api.createIdeaFeature(feature);
    setIdeaFeatures(prev => ({
      ...prev,
      [newFeature.idea_id]: [...(prev[newFeature.idea_id] || []), newFeature],
    }));
    return newFeature;
  }, []);

  const updateIdeaFeature = useCallback(async (id: string, feature: Partial<IdeaFeature>) => {
    const updated = await api.updateIdeaFeature(id, feature);
    setIdeaFeatures(prev => {
      const list = prev[updated.idea_id] || [];
      return {
        ...prev,
        [updated.idea_id]: list.map(f => (f.id === updated.id ? updated : f)),
      };
    });
    return updated;
  }, []);

  const deleteIdeaFeature = useCallback(async (id: string) => {
    let parentId: string | null = null;
    Object.keys(ideaFeatures).forEach((ideaId) => {
      if (ideaFeatures[ideaId]?.some((f) => f.id === id)) {
        parentId = ideaId;
      }
    });

    await api.deleteIdeaFeature(id);

    if (parentId) {
      setIdeaFeatures((prev) => ({
        ...prev,
        [parentId as string]: prev[parentId as string].filter((f) => f.id !== id),
      }));
    }
  }, [ideaFeatures]);

  const promoteIdeaToProject = useCallback(async (id: string) => {
    try {
      const idea = ideas.find(i => i.id === id);
      if (!idea) throw new Error('Idea not found');

      // 1. Create project from idea
      const newProj = await apiCreateProject({
        title: idea.title,
        description: idea.description ?? null,
        status: 'todo',
        created_by: user?.id,
      });

      // 2. Load idea features
      const features = await apiGetIdeaFeatures(id);

      // 3. Create tasks (subtasks) for each feature
      for (const f of features) {
        await apiCreateTask({
          title: f.title,
          description: f.link ?? null,
          category: 'task',
          status: 'todo',
          project_id: newProj.id,
          user_id: user?.id!,
        } as any);
      }

      // 4. Update idea to archived and mark promoted id
      await updateIdea(id, { archived: true, promoted_to_project_id: newProj.id });

      // 5. Refresh projects and tasks lists to include new data
      loadProjects();
      loadTasks();

      alert('Project created successfully!');
    } catch (error) {
      console.error('Failed to convert idea to project', error);
      alert('Failed to convert idea to project');
    }
  }, [ideas, user, updateIdea, loadProjects, loadTasks]);

  // Decision Tree
  const loadDecisions = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await api.getDecisions(user.id);
      setDecisions(data || []);
    } catch (error) {
      console.error('loadDecisions error:', error, JSON.stringify(error, null, 2));
    }
  }, [user?.id]);

  const createDecision = useCallback(async (decision: Omit<Decision, 'id' | 'created_at' | 'updated_at' | 'user_id'> & { user_id?: string }) => {
    const payload = { ...decision, user_id: decision.user_id ?? user?.id } as any;
    const newDecision = await api.createDecision(payload);
    setDecisions(prev => [...prev, newDecision]);
    return newDecision;
  }, [user]);

  const updateDecision = useCallback(async (id: string, decision: Partial<Omit<Decision, 'id' | 'created_at' | 'updated_at' | 'user_id'>>) => {
    const updated = await api.updateDecision(id, decision);
    setDecisions(prev => prev.map(d => d.id === id ? updated : d));
    return updated;
  }, []);

  const deleteDecision = useCallback(async (id: string) => {
    await api.deleteDecision(id);
    setDecisions(prev => prev.filter(d => d.id !== id));
    setDecisionOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[id];
      return newOptions;
    });
  }, []);

  // Decision Options
  const loadDecisionOptions = useCallback(async (decisionId: string) => {
    const data = await api.getDecisionOptions(decisionId);
    setDecisionOptions(prev => ({ ...prev, [decisionId]: data || [] }));
  }, []);

  const createDecisionOption = useCallback(async (option: Omit<DecisionOption, 'id' | 'created_at' | 'updated_at'>) => {
    const newOption = await api.createDecisionOption(option);
    setDecisionOptions(prev => ({
      ...prev,
      [newOption.decision_id]: [...(prev[newOption.decision_id] || []), newOption],
    }));
    return newOption;
  }, []);

  const updateDecisionOption = useCallback(async (id: string, option: Partial<Omit<DecisionOption, 'id' | 'created_at' | 'updated_at'>>) => {
    const updated = await api.updateDecisionOption(id, option);
    setDecisionOptions(prev => {
      const list = prev[updated.decision_id] || [];
      return {
        ...prev,
        [updated.decision_id]: list.map(o => o.id === updated.id ? updated : o),
      };
    });
    return updated;
  }, []);

  const deleteDecisionOption = useCallback(async (id: string) => {
    // Find parent decision id
    let parentId: string | null = null;
    Object.keys(decisionOptions).forEach((decisionId) => {
      if (decisionOptions[decisionId]?.some((o) => o.id === id)) {
        parentId = decisionId;
      }
    });
    await api.deleteDecisionOption(id);
    if (parentId) {
      setDecisionOptions(prev => ({
        ...prev,
        [parentId as string]: prev[parentId as string].filter((o) => o.id !== id),
      }));
    }
  }, [decisionOptions]);

  // Decision Option Points
  const loadDecisionOptionPoints = useCallback(async (optionId: string) => {
    const data = await api.getDecisionOptionPoints(optionId);
    setDecisionOptionPoints(prev => ({ ...prev, [optionId]: data || [] }));
  }, []);

  const createDecisionOptionPoint = useCallback(async (point: Omit<DecisionOptionPoint, 'id' | 'created_at'>) => {
    const newPoint = await api.createDecisionOptionPoint(point);
    setDecisionOptionPoints(prev => ({
      ...prev,
      [newPoint.option_id]: [...(prev[newPoint.option_id] || []), newPoint],
    }));
    return newPoint;
  }, []);

  const updateDecisionOptionPoint = useCallback(async (id: string, point: Partial<Omit<DecisionOptionPoint, 'id' | 'created_at'>>) => {
    const updated = await api.updateDecisionOptionPoint(id, point);
    setDecisionOptionPoints(prev => {
      const list = prev[updated.option_id] || [];
      return {
        ...prev,
        [updated.option_id]: list.map(p => p.id === updated.id ? updated : p),
      };
    });
    return updated;
  }, []);

  const deleteDecisionOptionPoint = useCallback(async (id: string) => {
    // Find parent option id
    let parentId: string | null = null;
    Object.keys(decisionOptionPoints).forEach((optionId) => {
      if (decisionOptionPoints[optionId]?.some((p) => p.id === id)) {
        parentId = optionId;
      }
    });
    await api.deleteDecisionOptionPoint(id);
    if (parentId) {
      setDecisionOptionPoints(prev => ({
        ...prev,
        [parentId as string]: prev[parentId as string].filter((p) => p.id !== id),
      }));
    }
  }, [decisionOptionPoints]);

  const value = {
    tasks,
    loadTasks,
    createTask,
    updateTask,
    deleteTask,
    promoteTaskToProject,
    demoteProjectToTask,
    notebooks,
    loadNotebooks,
    createNotebook,
    updateNotebook,
    deleteNotebook,
    notes,
    loadNotes,
    createNote,
    updateNote,
    deleteNote,
    projects,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    ideas,
    loadIdeas,
    createIdea,
    updateIdea,
    deleteIdea,
    loadIdeaFeatures,
    createIdeaFeature,
    updateIdeaFeature,
    deleteIdeaFeature,
    promoteIdeaToProject,
    ideaFeatures,
    decisions,
    loadDecisions,
    createDecision,
    updateDecision,
    deleteDecision,
    decisionOptions,
    loadDecisionOptions,
    createDecisionOption,
    updateDecisionOption,
    deleteDecisionOption,
    decisionOptionPoints,
    loadDecisionOptionPoints,
    createDecisionOptionPoint,
    updateDecisionOptionPoint,
    deleteDecisionOptionPoint,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
}; 